<template>
  <div class="pdf-translate-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="section-container">
        <h1 class="hero-title">{{ $t('category.pdf.hero.title') }}</h1>
        <p class="hero-subtitle">
          {{ $t('category.pdf.hero.subtitle') }}
        </p>
        <div class="hero-features">
          <div class="feature-tag">
            <Icon name="simple-icons:adobeacrobatreader" size="16" />
            {{ $t('category.pdf.features.focus') }}
          </div>
          <div class="feature-tag">
            <Icon name="material-symbols:format-paint" size="16" />
            {{ $t('category.common.features.format') }}
          </div>
          <div class="feature-tag">
            <Icon name="material-symbols:speed" size="16" />
            {{ $t('category.common.features.fast') }}
          </div>
          <div class="feature-tag">
            <Icon name="material-symbols:security" size="16" />
            {{ $t('category.common.features.secure') }}
          </div>
        </div>
      </div>
    </section>

    <!-- 翻译功能区域 -->
    <section class="translation-section">
      <div class="section-container">
        <CategoryTranslationForm
          category="pdf"
          @start-translation="handleStartTranslation"
        />
      </div>
    </section>

    <!-- SEO Content Section -->
    <section class="seo-section">
      <div class="section-container">
        <CategorySeoContent category="pdf" />
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
// SEO Meta
const { t } = useI18n()
useWebsiteSeo({
  title: () => t('seo.category.pdf.title'),
  description: () => t('seo.category.pdf.desc')
})

// 组件导入
import CategoryTranslationForm from '~/components/shared/CategoryTranslationForm.vue'
import CategorySeoContent from '~/components/shared/CategorySeoContent.vue'

// 事件处理（第一阶段静态展示）
const handleStartTranslation = (settings: any) => {
  // 真实链路已在表单内部发起；此处仅保留日志以便排查
  console.log('PDF翻译页面 - 开始翻译(已提交):', settings)
}
</script>

<style scoped lang="scss">
@use 'sass:color';

.pdf-translate-page {
  min-height: 100vh;
  background: linear-gradient(180deg,
    color.adjust(#4A90E2, $lightness: 35%) 0%,
    color.adjust(#4A90E2, $lightness: 42%) 15%,
    #f8f9fa 25%,
    color.adjust(#f8f9fa, $lightness: -3%) 40%,
    color.adjust($accent-color, $lightness: 38%) 60%,
    color.adjust($accent-color, $lightness: 35%) 75%,
    color.adjust(#4A90E2, $lightness: 37%) 100%);
}

// Hero区域
.hero-section {
  background:
    linear-gradient(135deg,
      rgba(color.adjust(#4A90E2, $lightness: 28%), 0.9) 0%,
      rgba(color.adjust(#4A90E2, $lightness: 32%), 0.85) 25%,
      rgba(color.adjust(#4A90E2, $lightness: 35%), 0.8) 50%,
      rgba(color.adjust(#4A90E2, $lightness: 28%), 0.85) 75%,
      rgba(color.adjust(#4A90E2, $lightness: 32%), 0.9) 100%),
    url('/images/pdf/pdf-hero-bg.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  padding: 4rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;

  .section-container {
    position: relative;
    z-index: 1;
    text-align: center;
  }

  .hero-title {
    font-size: 3rem;
    font-weight: 800;
    color: #ffffff;
    margin: 0 0 1.5rem 0;
    line-height: 1.2;
    text-shadow: 0 2px 4px rgba(#000000, 0.5);
  }

  .hero-subtitle {
    font-size: 1.25rem;
    color: #ffffff;
    margin: 0 0 3rem 0;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
    text-shadow: 0 1px 2px rgba(#000000, 0.3);
    opacity: 0.95;
  }

  .hero-features {
    display: flex;
    justify-content: center;
    gap: 1rem;
    flex-wrap: wrap;

    .feature-tag {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      background: rgba(#ffffff, 0.9);
      padding: 0.5rem 1rem;
      border-radius: 8px;
      font-size: 0.9rem;
      font-weight: 500;
      color: #6c757d;
      box-shadow: 0 1px 3px rgba(0,0,0,0.12);
      transition: transform 0.2s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 6px rgba(0,0,0,0.15);
      }
    }
  }
}

// 区域样式
section {
  padding: 4rem 0;

  .section-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1.5rem;
  }
}

.translation-section {
  background: linear-gradient(135deg,
    color.adjust(#4A90E2, $lightness: 37%) 0%,
    #f8f9fa 25%,
    color.adjust(#f8f9fa, $lightness: -2%) 50%,
    color.adjust($accent-color, $lightness: 38%) 75%,
    color.adjust($accent-color, $lightness: 35%) 100%);
  position: relative;
}

.seo-section {
  background: linear-gradient(135deg,
    color.adjust($accent-color, $lightness: 35%) 0%,
    color.adjust($accent-color, $lightness: 38%) 20%,
    #f8f9fa 40%,
    color.adjust(#f8f9fa, $lightness: -3%) 60%,
    color.adjust(#4A90E2, $lightness: 42%) 80%,
    color.adjust(#4A90E2, $lightness: 37%) 100%);
  position: relative;
}

// 响应式设计
@media (max-width: 768px) {
  .hero-section {
    padding: 3rem 0;

    .hero-title {
      font-size: 2rem;
    }

    .hero-subtitle {
      font-size: 1.1rem;
    }

    .hero-features {
      .feature-tag {
        font-size: 0.8rem;
        padding: 0.5rem 0.75rem;
      }
    }
  }

  section {
    padding: 1.5rem 0;

    .section-container {
      padding: 0 1rem;
    }
  }
}
</style>
